import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import ImageSlider from '@/components/ImageSlider';

const HomePage = () => {
  return (
    <div data-testid="home-page">
      <SEO 
        description="Expert sustainable development consultancy in Suriname. Specializing in project management training, M&E support, responsible mining, environmental policy, and organizational capacity building across the Guiana Shield."
        keywords="sustainable development Suriname, project management training, monitoring evaluation, responsible mining Suriname, environmental consultancy, NGO capacity building"
        url="https://ndebusiness.com/"
      />
      <section
        className="hero-section"
        style={{ backgroundImage: 'url(/hero-image.jpg)', backgroundPosition: 'center 30%' }}
      >
        <div className="hero-overlay"></div>
        <div className="content-container">
          <div className="hero-content fade-in">
            <h1 className="hero-title" data-testid="hero-title">
              DELIVERING RESULTS THAT MATTER
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              From national policy to community-level transformation, our work creates measurable, lasting impact across Suriname and the Guiana Shield.
            </p>
            <Link to="/services" className="btn-primary" data-testid="hero-cta-button">
              Read More
            </Link>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="content-container">
          <h2 className="font-rubik text-3xl md:text-4xl font-bold mb-8 text-[#aaae10] text-center" data-testid="background-title">
            BACKGROUND
          </h2>
          <div className="font-roboto text-base leading-relaxed grid grid-cols-1 md:grid-cols-2 gap-6">
            <p>
              NDE Training & Consultancy was established on May 11th, 2023. The company is
              committed to practice biblical principles such as mutual respect, servitude
              and adding value to businesses, organizations and institutes. We are dedicated
              to execute all assignments with the same passion and dedication to deliver
              excellent results.
            </p>
            <p>
              The company was established from a clear vision: to create an environment where
              excellence, strong leadership, and continuous growth are the standard. My
              professional journey highlighted the need for organizations that not only
              deliver results, but also invest in people and their development.
            </p>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-[#f5f5f5]">
        <div className="content-container">
          <h2 className="font-rubik text-3xl md:text-4xl font-bold mb-8 text-[#aaae10] text-center" data-testid="ceo-title">
            MEET THE CEO OF THE COMPANY
          </h2>
          <div className="font-roboto text-base leading-relaxed grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <p>
                <strong>Nathalie Daniëlle Emanuels</strong> is a seasoned sustainable
                development professional, mining specialist, and strategic leader with over two
                decades of experience at the forefront of environmental management, responsible
                mining, and community-driven development in Suriname and the wider Guiana
                Shield.
              </p>
              <p>
                With a strong academic foundation in Mining (BSc) and a Master of Science in
                Sustainable Development, Nathalie has built a career that bridges{' '}
                <strong>technical expertise, policy development, and social impact</strong>.
                Her work is deeply rooted in the belief that economic growth and environmental
                stewardship must go hand in hand—particularly in resource-rich countries.
              </p>
            </div>
            <div>
              <p>
                Throughout her career, Nathalie has held senior leadership roles, including
                Conservation Director at WWF Guianas and Program Manager at the Suriname
                Conservation Foundation. She currently serves as the CEO of NDE Training &
                Consultancy, where she leads high-impact projects for governments,
                international organizations, and local communities.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="content-container">
          <h2 className="font-rubik text-3xl md:text-4xl font-bold mb-8 text-[#aaae10] text-center" data-testid="team-title">
            THE TEAM
          </h2>
          <div className="font-roboto text-base leading-relaxed max-w-4xl mx-auto">
            <p>
              The team contains a core team of professionals who are available on call and depending on the type of assignment/project more expertise is hired. Full-time support is provided by Ms. Moïegia Pahalwankhan who provides financial, administrative and logistic support.
            </p>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-[#f5f5f5]">
        <div className="content-container">
          <h2 className="font-rubik text-3xl md:text-4xl font-bold mb-8 text-[#aaae10] text-center" data-testid="sets-apart-title">
            WHAT SETS US APART
          </h2>
          <div className="font-roboto text-base leading-relaxed grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <p>
                What sets the organization apart is the ability to connect high-level strategy
                with real-world implementation. The organization has successfully:
              </p>
              <ul className="list-disc pl-8 space-y-2">
                <li>Led multi-million-dollar project development (including a USD 29.6M GEF proposal)</li>
                <li>Conducted national-level evaluations and baseline studies</li>
                <li>Designed training programs and curricula in environmental management and entrepreneurship</li>
                <li>Worked closely with governments, NGOs, international agencies, and local communities</li>
              </ul>
            </div>
            <div className="space-y-4">
              <p>
                Our approach is collaborative, inclusive, and results-driven—ensuring that
                development initiatives are not only effective, but also equitable and
                sustainable.
              </p>
              <div className="bg-[#aaae10] text-white p-6 rounded-lg mt-6">
                <p className="font-rubik text-xl font-semibold text-center">
                  At the core of our work is a clear mission: to drive sustainable development
                  that empowers people, protects natural resources, and creates long-term value
                  for society.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="content-container">
          <h2 className="font-rubik text-3xl md:text-4xl font-bold mb-6 text-center text-[#aaae10]">
            OUR IMPACT IN ACTION
          </h2>
          <p className="font-roboto text-lg text-center mb-8 max-w-3xl mx-auto text-gray-700">
            See how we're making a difference across Suriname through conservation, community engagement, and sustainable development.
          </p>
        </div>
        <ImageSlider />
      </section>

      <section className="section-spacing bg-[#646464] text-white">
        <div className="content-container">
          <h2 className="font-rubik text-3xl md:text-4xl font-bold mb-6 text-center" data-testid="how-we-work-title">
            HOW WE WORK
          </h2>
          <p className="font-roboto text-lg mb-8 text-center">
            We believe sustainable development must work in practice—not just on paper.
            That's why we combine expertise, collaboration, and action to deliver solutions
            that create real and lasting change.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white text-black p-6 rounded-lg">
              <h3 className="font-rubik text-xl font-bold mb-3 text-[#aaae10]">
                1. Understanding the Context
              </h3>
              <p className="font-roboto text-sm leading-relaxed">
                Every project begins with a deep understanding of the environment in which we
                operate through baseline studies, stakeholder mapping, and data analysis.
              </p>
            </div>

            <div className="bg-white text-black p-6 rounded-lg">
              <h3 className="font-rubik text-xl font-bold mb-3 text-[#aaae10]">
                2. Co-Creation & Stakeholder Engagement
              </h3>
              <p className="font-roboto text-sm leading-relaxed">
                We actively involve government institutions, local communities, private
                sector actors, and NGOs to co-create inclusive solutions.
              </p>
            </div>

            <div className="bg-white text-black p-6 rounded-lg">
              <h3 className="font-rubik text-xl font-bold mb-3 text-[#aaae10]">
                3. Strategic Design & Planning
              </h3>
              <p className="font-roboto text-sm leading-relaxed">
                We design tailored strategies and programs through policy development,
                project design, and program planning with clear objectives.
              </p>
            </div>

            <div className="bg-white text-black p-6 rounded-lg">
              <h3 className="font-rubik text-xl font-bold mb-3 text-[#aaae10]">
                4. Implementation & Capacity Building
              </h3>
              <p className="font-roboto text-sm leading-relaxed">
                We ensure plans are effectively implemented through training, tool
                development, and on-the-ground technical support.
              </p>
            </div>

            <div className="bg-white text-black p-6 rounded-lg">
              <h3 className="font-rubik text-xl font-bold mb-3 text-[#aaae10]">
                5. Monitoring, Evaluation & Learning
              </h3>
              <p className="font-roboto text-sm leading-relaxed">
                We integrate monitoring frameworks and performance tracking to ensure
                transparency, effectiveness, and real impact.
              </p>
            </div>

            <div className="bg-white text-black p-6 rounded-lg">
              <h3 className="font-rubik text-xl font-bold mb-3 text-[#aaae10]">
                6. Long-Term Impact & Sustainability
              </h3>
              <p className="font-roboto text-sm leading-relaxed">
                Our ultimate goal is to create solutions that endure by prioritizing
                environmental sustainability, economic viability, and social inclusion.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
